import dayjs from 'dayjs';
import 'dayjs/locale/en';
import 'dayjs/locale/es';
import 'dayjs/locale/it';
import React, {
  createContext,
  Dispatch,
  ReducerAction,
  ReducerState,
  useCallback,
  useContext,
  useReducer,
} from 'react';
import { getCommonTxs, LangKey, TxCommon } from '../intl';
import services from '../services/services';

// State

type IntlState = {
  lang: LangKey;
  common: TxCommon;
};

// get initial language from preferences ( local storage ) or use English
const initialLang = services.preferences.getLang() ?? 'en';

dayjs.locale(initialLang);

const initialState: IntlState = {
  lang: initialLang,
  common: getCommonTxs(initialLang),
};

// Actions

type SetLangAction = {
  type: 'SET_LANG';
  lang: LangKey;
};

const SET_LANG_ACTION_TYPE = 'SET_LANG';

export function setLangAction(lang: LangKey): SetLangAction {
  return {
    type: SET_LANG_ACTION_TYPE,
    lang,
  };
}

type IntlAction = SetLangAction;

// Reducer

type IntlStateReducer = (state: IntlState, action: IntlAction) => IntlState;

function intlReducer(state: IntlState, action: IntlAction): IntlState {
  if (action.type === SET_LANG_ACTION_TYPE) {
    return {
      lang: action.lang,
      common: getCommonTxs(action.lang),
    };
  }

  return state;
}

// Context

type IntlContextState = [ReducerState<IntlStateReducer>, Dispatch<ReducerAction<IntlStateReducer>>];
const IntlContext = createContext<IntlContextState>([initialState, () => {}]);

// Provider

export const IntlProvider: React.FC = ({ children }) => {
  const [state, dispatch] = useReducer(intlReducer, initialState);

  return <IntlContext.Provider value={[state, dispatch]}>{children}</IntlContext.Provider>;
};

export const useIntl = () => {
  const [state, dispatch] = useContext<IntlContextState>(IntlContext);

  const setLang = useCallback(
    (lang: LangKey) => {
      dispatch(setLangAction(lang));
      services.preferences.setLang(lang);
      dayjs.locale(lang);
    },
    [dispatch]
  );

  return {
    lang: state.lang,
    txc: getCommonTxs(state.lang),
    setLang,
  };
};
